import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Hotel, Inbox, ShieldCheck, BarChart3, Plus, Pencil, BadgePercent, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PropertyForm } from "../components/hotelspulse/PropertyForm";
import { LeadInbox } from "../components/hotelspulse/LeadInbox";
import { AdminQueue } from "../components/hotelspulse/AdminQueue";
import { HPHeader } from "../components/hotelspulse/HPHeader";
import { HPFooter } from "../components/hotelspulse/HPFooter";
import { PageBuilderAI } from "../components/hotelspulse/PageBuilderAI";

const STATUS_BADGE = { pending: "bg-amber-100 text-amber-800", published: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700", suspended: "bg-zinc-200 text-zinc-600" };

const OfferAdder = ({ property, onDone }) => {
  const [title, setTitle] = useState("");
  const add = async () => {
    if (!title.trim()) return;
    try { await api.post(`/directstay/properties/${property.id}/offers`, { title }); toast.success("Offer published"); setTitle(""); onDone(); }
    catch { toast.error("Could not add offer"); }
  };
  return (
    <div className="flex gap-2 mt-2">
      <input data-testid={`offer-input-${property.id}`} className="flex-1 border border-zinc-300 px-3 py-1.5 text-xs" placeholder="e.g. 10% off direct bookings + free breakfast" value={title} onChange={(e) => setTitle(e.target.value)} />
      <button data-testid={`offer-add-${property.id}`} onClick={add} className="bg-amber-400 text-emerald-950 text-xs font-700 px-3">Add offer</button>
    </div>
  );
};

export default function HotelsPulseDashboard() {
  const { user } = useAuth();
  const isMod = ["moderator", "admin"].includes(user?.role);
  const [tab, setTab] = useState("properties");
  const [config, setConfig] = useState(null);
  const [props, setProps] = useState(null);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | property object
  const [builder, setBuilder] = useState(null); // null | property object (AI Page Builder)

  const load = () => {
    api.get("/directstay/my/properties").then((r) => setProps(r.data.items)).catch(() => setProps([]));
    api.get("/directstay/my/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { api.get("/directstay/config").then((r) => setConfig(r.data)).catch(() => {}); load(); }, []);

  const TABS = [["properties", Hotel, "My Properties"], ["leads", Inbox, "Lead Inbox"], ...(isMod ? [["admin", ShieldCheck, "Verification Queue"]] : [])];

  return (
    <div data-testid="hotelspulse-dashboard" className="min-h-screen bg-zinc-50">
      <HPHeader />
      <section className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-2">HotelsPulse Partner Dashboard</div>
          <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tight">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 max-w-2xl">
              {[["Properties", stats.properties], ["Page views", stats.page_views], ["Total leads", stats.total_leads], ["Booked", `${stats.booked} (${stats.conversion}%)`]].map(([k, v]) => (
                <div key={k} className="bg-emerald-900/60 border border-emerald-800 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-300">{k}</div>
                  <div className="font-heading font-900 text-xl">{v}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex gap-1 border-b border-zinc-300 mb-6 overflow-x-auto">
          {TABS.map(([key, Icon, label]) => (
            <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-600 whitespace-nowrap border-b-2 -mb-px ${tab === key ? "border-emerald-700 text-emerald-800" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "properties" && (
          <div>
            {editing ? (
              <PropertyForm config={config} existing={editing === "new" ? null : editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            ) : builder ? (
              <PageBuilderAI property={builder} onClose={() => setBuilder(null)} onApplied={() => { setBuilder(null); load(); }} />
            ) : (
              <>
                <button data-testid="new-property-btn" onClick={() => setEditing("new")} className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-700 px-5 py-2.5 text-sm uppercase tracking-wide mb-6">
                  <Plus className="w-4 h-4" /> List a property
                </button>
                {props === null ? <div className="text-zinc-400 py-8">Loading…</div> : props.length === 0 ? (
                  <div className="border border-dashed border-zinc-300 bg-white p-12 text-center">
                    <Hotel className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                    <div className="font-heading font-900 text-xl">No properties yet</div>
                    <p className="text-zinc-500 mt-1 text-sm">List your hotel, homestay, resort or houseboat — it's free during the pilot.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {props.map((p) => (
                      <div key={p.id} data-testid={`my-prop-${p.id}`} className="border border-zinc-300 bg-white p-5">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] font-700 uppercase px-2 py-0.5 ${STATUS_BADGE[p.status] || "bg-zinc-100"}`}>{p.status}</span>
                              <span className="text-xs text-zinc-400 uppercase">{(p.property_type || "").replace(/_/g, " ")}</span>
                            </div>
                            <div className="font-heading font-900 text-lg mt-1">{p.name}</div>
                            <div className="text-sm text-zinc-500">{p.location?.city}, {p.location?.state} · {p.stats?.views || 0} views · {p.stats?.leads || 0} leads</div>
                            {p.status === "published" && <Link to={`/hotelspulse/stay/${p.slug}`} className="text-xs text-emerald-700 underline">View public page</Link>}
                          </div>
                          <div className="flex gap-2">
                            <button data-testid={`ai-builder-${p.id}`} onClick={() => setBuilder(p)} className="inline-flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-emerald-950 px-3 py-1.5 text-xs font-700"><Sparkles className="w-3.5 h-3.5" /> AI Page Builder</button>
                            <button data-testid={`edit-prop-${p.id}`} onClick={() => setEditing(p)} className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs font-600 hover:border-emerald-600"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                          </div>
                        </div>
                        {p.status === "published" && (
                          <div className="mt-3 border-t border-zinc-100 pt-3">
                            <div className="text-xs font-600 uppercase text-zinc-400 flex items-center gap-1.5"><BadgePercent className="w-3.5 h-3.5" /> Direct offers ({(p.offers || []).length})</div>
                            {(p.offers || []).map((o) => (
                              <div key={o.id} className="flex items-center justify-between text-sm mt-1.5">
                                <span>{o.title}</span>
                                <button onClick={async () => { await api.delete(`/directstay/properties/${p.id}/offers/${o.id}`); load(); }} className="text-xs text-red-500 hover:underline">Remove</button>
                              </div>
                            ))}
                            <OfferAdder property={p} onDone={load} />
                          </div>
                        )}
                        {p.status === "pending" && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 mt-3">Under review — our team verifies every listing before it goes live on the network.</p>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {tab === "leads" && <LeadInbox />}
        {tab === "admin" && isMod && <AdminQueue config={config} />}
      </div>
      <HPFooter />
    </div>
  );
}
